import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueCoordinates} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import type {EnrichedIssueListItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {useState} from 'react'

import {issueCoordinatesFromUrl, usePreheatIssueRows} from './use-preheat-issue-rows'

type IssuePreheatRow = Pick<EnrichedIssueListItem, 'number' | 'permalink' | 'repoNameWithOwner'> & {
  itemType?: 'issue' | 'pull_request'
}

type Props = {
  rows: readonly IssuePreheatRow[]
  listIdentity: string
  source: string
}

export function IssueRowsPreheater({rows, listIdentity, source}: Props) {
  const login = getCurrentUserLogin()
  if (
    !login ||
    !isLoggedIn() ||
    !isFeatureEnabled('issue_viewer_paved_path') ||
    isFeatureEnabled('disable_preheating_for_issues')
  )
    return null

  const issuesByUrl = new Map<string, IssueCoordinates>()
  for (const row of rows) {
    if (row.itemType !== 'issue') continue
    const issue = issueCoordinatesFromUrl(row.permalink)
    if (!issue || issue.number !== row.number || `${issue.owner}/${issue.repo}` !== row.repoNameWithOwner) continue
    issuesByUrl.set(
      `/${encodeURIComponent(issue.owner)}/${encodeURIComponent(issue.repo)}/issues/${issue.number}`,
      issue,
    )
  }
  if (issuesByUrl.size === 0) return null

  // Only the render-null subtree resets. Metadata updates must not restart tasks or remount the visible list.
  const identity = JSON.stringify([login, listIdentity, [...issuesByUrl.keys()]])
  return <IssueRowsPreheatTasks key={identity} issues={[...issuesByUrl.values()]} login={login} source={source} />
}

function IssueRowsPreheatTasks({issues: initialIssues, login, source}: Parameters<typeof usePreheatIssueRows>[0]) {
  // The key remounts this component whenever the coordinates change.
  const [issues] = useState(initialIssues)
  usePreheatIssueRows({issues, login, source})
  return null
}
